import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Campaign,
  LinkedCategory,
  CategoryType,
  Attributes,
} from "@vviinn/user-managment-client-ts";
import { FetchableState } from "..";
import {
  fetchCampaigns,
  fetchCampaignsAttributes,
  fetchCampaignsFilters,
} from "./fetch";
import { upsertCampaign } from "./upsert";
import { deleteCampaign } from "./delete";
import { upsertBoosting } from "./boostingRequests";
import { changeActiveSite } from "../site/site-slice";
import { orderStringArray } from "../../../helpers/array/orderStringArray";
import { SourceOrTarget } from "../../../campaign/types";

type CampaignsState = FetchableState & {
  campaigns: Campaign[];
  attributes: Attributes;
  filters: string[];
};

const initialState: CampaignsState = {
  campaigns: [],
  attributes: {
    productAttributes: [],
    imageAttributes: [],
  },
  filters: [],
  loading: false,
  error: false,
  status: "idle",
};

const campaignsSlice = createSlice({
  name: "campaigns",
  initialState,
  reducers: {
    addLinkedCategory(
      state,
      {
        payload,
      }: PayloadAction<{
        campaignId: Campaign["id"];
        category: LinkedCategory;
        type: SourceOrTarget;
        categoryType: CategoryType;
      }>,
    ) {
      const { campaignId, category, type, categoryType } = payload;
      const index = state.campaigns.findIndex((c) => c.id === campaignId);

      state.campaigns[index]?.linkedCategories[type]?.[
        categoryType === "google_product_category"
          ? "googleFeedCategories"
          : "productTypeCategories"
      ]?.push(category);
    },
    removeLinkedCategory(
      state,
      {
        payload,
      }: PayloadAction<{
        campaignId: Campaign["id"];
        category: LinkedCategory;
        type: SourceOrTarget;
        categoryType: CategoryType;
      }>,
    ) {
      const { campaignId, category, type, categoryType } = payload;
      const campaign = state.campaigns.find((c) => c.id === campaignId);

      const isCategoryCodeEqual = (c: LinkedCategory) =>
        c.code !== category.code;

      const filterCategories = (categories?: LinkedCategory[]) =>
        categories ? categories.filter(isCategoryCodeEqual) : [];

      if (!campaign) return;

      const currentLinkedCategories = campaign.linkedCategories[type];

      if (!currentLinkedCategories) return;

      const feedCategoryType =
        categoryType === "product_type"
          ? "productTypeCategories"
          : "googleFeedCategories";

      const filtered = filterCategories(
        currentLinkedCategories[feedCategoryType],
      );

      currentLinkedCategories[feedCategoryType] = filtered;
    },
    removeBoostingFromAllCampaigns(state, { payload: boostingId }: PayloadAction<string>) {
      for (const c of state.campaigns) {
        if (!c.boosting?.linkedBoostingCampaigns) continue;
        c.boosting.linkedBoostingCampaigns = c.boosting.linkedBoostingCampaigns.filter(
          (lbc) => lbc.campaign?.id !== boostingId,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCampaigns.pending, (state) => {
      state.error = false;
      state.loading = true;
      state.status = "loading";
    });

    builder.addCase(fetchCampaigns.rejected, (state) => {
      state.error = true;
      state.loading = false;
      state.status = "error";
      state.campaigns = [];
    });

    builder.addCase(fetchCampaigns.fulfilled, (state, { payload }) => {
      state.error = false;
      state.loading = false;
      state.campaigns = payload;
      state.status = "fulfilled";
    });

    builder.addCase(
      fetchCampaignsAttributes.fulfilled,
      (state, { payload }) => {
        const typecastedAttributes = payload as Attributes;
        const orderedAttributes = {
          productAttributes: orderStringArray(
            typecastedAttributes.productAttributes,
          ),
          imageAttributes: typecastedAttributes.imageAttributes,
        };
        state.attributes = orderedAttributes;
      },
    );

    builder.addCase(fetchCampaignsFilters.fulfilled, (state, { payload }) => {
      state.filters = payload;
    });

    builder.addCase(fetchCampaignsFilters.rejected, (state) => {
      state.filters = [];
    });

    builder.addCase(upsertCampaign.fulfilled, (state, { payload }) => {
      if (!payload) return;
      const { id } = payload;
      const campaignIndex = state.campaigns.findIndex((c) => c.id === id);

      if (campaignIndex === -1) {
        state.campaigns.push(payload);
      } else {
        state.campaigns[campaignIndex] = payload;
      }
    });

    builder.addCase(deleteCampaign.fulfilled, (state, { payload }) => {
      state.campaigns = state.campaigns.filter(
        (c) => c.id.toString() !== payload,
      );
    });
    builder.addCase(changeActiveSite, (state) => {
      if (!state.campaigns.length) return;
      state.campaigns = [];
      state.status = "idle";
    });

    builder.addCase(upsertBoosting.fulfilled, (state, { payload }) => {
      if (!payload?.id) return;
      for (const c of state.campaigns) {
        if (!c.boosting?.linkedBoostingCampaigns) continue;
        for (const lbc of c.boosting.linkedBoostingCampaigns) {
          if (lbc.campaign?.id === payload.id) {
            lbc.campaign = payload;
          }
        }
      }
    });

  },
});

export default campaignsSlice.reducer;
export const { addLinkedCategory, removeLinkedCategory, removeBoostingFromAllCampaigns } =
  campaignsSlice.actions;
